
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/ui/logo";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { SignupForm } from "@/components/student-auth/SignupForm";
import { LoginForm } from "@/components/student-auth/LoginForm";
import { CoursePreview } from "@/components/student-auth/CoursePreview";
import { StudentSidebar } from "@/components/student-auth/StudentSidebar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { devLog } from "@/utils/devLog";
import {
  getPendingEnrollment,
  getEnrollmentFromUrlParams,
  savePendingEnrollment,
  clearPendingEnrollment
} from "@/utils/pendingEnrollment";
import { useEnrollmentRedirect } from "@/hooks/auth/useEnrollmentRedirect";

const StudentAuth = () => {
  const location = useLocation();
  const { session, initialSessionProcessed } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"signup" | "login">("signup");
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  
  // Get enrollment data from URL or localStorage
  const pendingEnrollment = getPendingEnrollment();
  const urlEnrollment = getEnrollmentFromUrlParams(location.search);
  const { handleEnrollAfterAuth } = useEnrollmentRedirect(
    pendingEnrollment?.courseId || urlEnrollment?.courseId,
    pendingEnrollment?.price || urlEnrollment?.price,
    pendingEnrollment?.courseName || urlEnrollment?.courseName,
    pendingEnrollment?.username || urlEnrollment?.username,
    pendingEnrollment?.courseSlug || urlEnrollment?.courseSlug
  );
  
  // If URL has enrollment data, save it
  useEffect(() => {
    if (urlEnrollment && urlEnrollment.courseId) {
      savePendingEnrollment({
        ...urlEnrollment,
        from: location.pathname
      });
      devLog("[StudentAuth] Saved enrollment data from URL parameters", urlEnrollment);
    }
  }, [urlEnrollment]);
  
  // If already logged in and initial session is processed, handle enrollment
  useEffect(() => {
    if (initialSessionProcessed && session) {
      devLog("[StudentAuth] User already authenticated, redirecting to handle enrollment");
      handleEnrollAfterAuth();
    }
  }, [initialSessionProcessed, session, handleEnrollAfterAuth]);
  
  // Handle signup submission
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsLoading(true);
    
    try {
      if (password.length < 8) {
        throw new Error("Password must be at least 8 characters long");
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: fullName,
            full_name: fullName,
          },
        },
      });
      
      if (error) {
        // Check for the specific "user already registered" error
        if (error.message.includes("User already registered") || error.status === 422) {
          setFormError("An account with this email already exists. Please try logging in instead.");
          toast.error("Account already exists. Please log in.");
          setActiveTab("login");
          setIsLoading(false);
          return;
        }
        
        throw error;
      }
      
      if (data.session) {
        queryClient.clear();
        toast.success("Account created successfully");
        handleEnrollAfterAuth();
      } else {
        toast.success("Account created! Please check your email for confirmation");
        navigate("/login");
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error(error.message || "Failed to create account. Please try again.");
      setFormError(error.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle login submission
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      queryClient.clear();
      toast.success("Logged in successfully");
      handleEnrollAfterAuth();
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.message || "Failed to log in. Please try again.");
      setFormError(error.message || "Failed to log in. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex min-h-screen">
      {/* Sidebar with course preview */}
      <StudentSidebar>
        {pendingEnrollment && (
          <CoursePreview 
            courseId={pendingEnrollment.courseId} 
            courseName={pendingEnrollment.courseName}
            price={pendingEnrollment.price}
          />
        )}
      </StudentSidebar>
      
      {/* Main auth form */}
      <div className="flex-1 p-6 flex items-center justify-center bg-primary-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto mb-2">
              <Logo size="lg" />
            </div>
            
            <CardTitle className="text-2xl font-heading">
              {pendingEnrollment ? "One step away from learning" : "Join our learning platform"}
            </CardTitle>
            
            <CardDescription>
              {pendingEnrollment 
                ? `Create an account or sign in to access ${pendingEnrollment.courseName || 'this course'}`
                : "Create an account or log in to continue"}
            </CardDescription>
          </CardHeader>
          
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "signup" | "login")}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
              <TabsTrigger value="login">Log In</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signup" className="pt-4 pb-2 px-1">
              <SignupForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                fullName={fullName}
                setFullName={setFullName}
                loading={isLoading}
                formError={formError}
                onSubmit={handleSignup}
              />
            </TabsContent>
            
            <TabsContent value="login" className="pt-4 pb-2 px-1">
              <LoginForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                loading={isLoading}
                formError={formError}
                onSubmit={handleLogin}
              />
            </TabsContent>
          </Tabs>
          
          <CardFooter className="flex justify-center text-sm text-muted-foreground pb-6">
            By signing up, you agree to our Terms and Privacy Policy
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default StudentAuth;
